export class KeyValueStore {
  get() {
    return new Error(`calling get is not defined`);
  }

  set() {
    return new Error(`calling set is not defined`);
  }
}

class KeyPair {
  constructor(name, initialValue) {
    this.name = name;
    this.initialValue = initialValue;
  }
}

export const KeyValueStateManager = function (keyValue) {
  let _deps; // hold our state and dependencies in scope
  let _keyValue = keyValue;

  return {
    render(Component) {
      const Comp = Component();
      Comp.render();
      return Comp;
    },
    useEffect(callback, depArray) {
      const hasNoDeps = !depArray;
      const hasChangedDeps = _deps ? !depArray.every((el, i) => el === _deps[i]) : true;
      if (hasNoDeps || hasChangedDeps) {
        callback();
        _deps = depArray;
      }
    },
    useKey(keyName) {
      const _keyName = keyName;

      function setState(newVal) {
        _keyValue.set(_keyName, newVal);
      }
      return [_keyValue.get(_keyName), setState];
    },
  };
}; // Pass in keyValue

export class KeyValue {
  constructor(name, keyValueStore) {
    this.name = name;
    this.storage = new Map();
    this.keyPairs = new Map();
    this.keyValueStore = keyValueStore;
    this.restoreFromSessionStorage();
  }

  restoreFromSessionStorage() {
    const sessionStorageData = localStorage.getItem(this.name);
    if (sessionStorageData) {
      const parsedData = JSON.parse(sessionStorageData);
      this.storage = new Map(parsedData.storage);
      this.keyPairs = new Map(
        Object.entries(parsedData.keyPairs).map(([key, keyPairData]) => [
          key,
          new KeyPair(keyPairData.name, keyPairData.initialValue, keyPairData.lifetime),
        ]),
      );
    }
  }

  saveToSessionStorage() {
    const sessionStorageData = {
      storage: Array.from(this.storage.entries()),
      keyPairs: Object.fromEntries(
        Array.from(this.keyPairs.entries()).map(([key, keyPair]) => [
          key,
          {
            name: keyPair.name,
            initialValue: keyPair.initialValue,
            lifetime: keyPair.lifetime,
          },
        ]),
      ),
    };
    localStorage.setItem(this.name, JSON.stringify(sessionStorageData));
  }

  /*
    Must accept input json in the format as:
    [
      {
          "key": "showOrganization",
          "value": "false"
      },
      {
          "key": "temperatureUnits",
          "value": "F"
      }
    ] 

    key, and value will always be strings.
  */

  async initFromJSON(initData, initKeyPairs = {}) {
    initData &&
      initData.forEach((item) => {
        this.keyPairs.set(item.key, new KeyPair(item.key, item.value));
        this.storage.set(item.key, item.value);
        delete initKeyPairs[item.key];
      });
    this.saveToSessionStorage();

    const initKeys = Object.keys(initKeyPairs);
    initKeys.forEach((key) => {
      this.addKeyPair(key, initKeyPairs[key])
        .then(() => {
          this.saveToSessionStorage();
        })
        .catch(() => {});
    });

    return Promise.resolve(true);
  }

  async addKeyPair(key, initialValue) {
    if (!this.storage.get(key)) {
      this.keyPairs.set(key, new KeyPair(key, initialValue));
      this.storage.set(key, initialValue);
    }
    this.saveToSessionStorage();

    if (this.keyValueStore) {
      const value = await this.keyValueStore.get(key);
      if (value) {
        this.storage.set(key, value);
      } else {
        this.set(key, initialValue);
      }
    }

    this.saveToSessionStorage();

    return Promise.resolve(true);
  }

  get(key) {
    const keyPair = this.keyPairs.get(key);
    if (!keyPair) {
      throw new Error(`KeyPair not found for key: ${key}`);
    }
    let result = null;

    if (!result) {
      result = this.storage.get(key);
    }

    if (typeof result == 'string') {
      if (result.toLowerCase() == 'true') {
        return true;
      }
      if (result.toLowerCase() == 'false') {
        return false;
      }

      let test_number = Number(result);
      if (!isNaN(test_number)) {
        return test_number;
      }
    }

    return result;
  }

  set(key, value) {
    const keyPair = this.keyPairs.get(key);
    if (!keyPair) {
      throw new Error(`KeyPair not found for key: ${key}`);
    }

    if (value === null || value === undefined) {
      this.storage.delete(key);
    } else {
      this.storage.set(key, `${value}`);
    }

    if (this.keyValueStore) {
      if (value === '' || value === null || value === undefined) {
        this.keyValueStore.remove(key);
      } else {
        this.keyValueStore.set(key, `${value}`);
      }
    }
    this.saveToSessionStorage();
  }
}
